import React, { useEffect, useState } from "react";
import { supabase, fmtBRL, formatError } from "@/lib/supabase";
import { db } from "@/lib/db";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChevronDown, ChevronUp, Link as LinkIcon, X, Eye, Check } from "lucide-react";

const STATUS = {
  "em andamento": { label: "Em andamento", cls: "bg-blue-100 text-blue-800" },
  concluido: { label: "Concluído", cls: "bg-green-100 text-green-800" },
  pausado: { label: "Pausado", cls: "bg-yellow-100 text-yellow-800" },
};

export default function Projects() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [newTask, setNewTask] = useState("");
  const [form, setForm] = useState({ cliente: "", servico: "", valor: "", prazo: new Date().toISOString().split("T")[0], status: "em andamento", tasks: [], revisor_id: "", file_url: "" });
  const [team, setTeam] = useState([]);

  const isRevisor = user?.role === "revisor";

  const loadTeam = async () => {
    if (isRevisor) return;
    try {
      const sessionRaw = localStorage.getItem("revisapro.session.v2");
      const session = sessionRaw ? JSON.parse(sessionRaw) : null;
      const token = session?.token;
      const res = await fetch("/api/team", {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) setTeam(await res.json());
    } catch(e) {}
  };

  const reload = async () => {
    try { setItems((await db.select("projetos", { order: "created_at", ascending: false })) || []); }
    catch (err) { toast.error(formatError(err)); }
  };
  useEffect(() => { 
    if (user) {
      reload();
      loadTeam();
    } 
  }, [user]);

  const markAsSeen = async (p) => {
    if (!p.seen_by?.includes(user.id)) {
      const newSeen = [...(p.seen_by || []), user.id];
      try {
        const sessionRaw = localStorage.getItem("revisapro.session.v2");
        const token = sessionRaw ? JSON.parse(sessionRaw)?.token : null;
        await fetch(`/api/projects/${p.id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ seen_by: newSeen })
        });
        setItems(prev => prev.map(x => x.id === p.id ? { ...x, seen_by: newSeen } : x));
      } catch(e) {}
    }
  };

  const save = async () => {
    try {
      const data = { ...form, user_id: user.id };
      if (editingId) {
        await db.update("projetos", editingId, data);
        toast.success("Projeto atualizado");
      } else {
        await db.insert("projetos", data);
        toast.success("Projeto criado");
      }
      setOpen(false); setEditingId(null); resetForm(); reload();
    } catch (err) { toast.error(formatError(err)); }
  };

  const resetForm = () => {
    setForm({ cliente: "", servico: "", valor: "", prazo: new Date().toISOString().split("T")[0], status: "em andamento", tasks: [], revisor_id: "", file_url: "", delivery_url: "", revisor_comments: "" });
    setEditingId(null);
    setNewTask("");
  };

  const edit = (p) => {
    setForm({ 
      cliente: p.cliente, 
      servico: p.servico, 
      valor: p.valor ? String(p.valor).replace('.', ',') : "", 
      prazo: p.prazo, 
      status: p.status, 
      tasks: p.tasks || [],
      revisor_id: p.revisor_id || "",
      file_url: p.file_url || "",
      delivery_url: p.delivery_url || "",
      revisor_comments: p.revisor_comments || ""
    });
    setEditingId(p.id);
    setOpen(true);
    markAsSeen(p);
  };

  const updStatus = async (id, s) => { try { await db.update("projetos", id, { status: s }); reload(); } catch (err) { toast.error(formatError(err)); } };
  const rm = async (id) => { if(confirm("Excluir projeto?")) { try { await db.remove("projetos", id); reload(); } catch (err) { toast.error(formatError(err)); } } };

  const addTask = () => {
    if (!newTask.trim()) return;
    setForm({ ...form, tasks: [...form.tasks, { id: crypto.randomUUID(), text: newTask, completed: false }] });
    setNewTask("");
  };

  const removeTask = (tid) => {
    setForm({ ...form, tasks: form.tasks.filter(t => t.id !== tid) });
  };

  const toggleTask = async (p, tid) => {
    const updatedTasks = p.tasks.map(t => t.id === tid ? { ...t, completed: !t.completed } : t);
    try {
      await db.update("projetos", p.id, { tasks: updatedTasks });
      reload();
    } catch (err) { toast.error(formatError(err)); }
  };

  const getProgress = (tasks) => {
    if (!tasks || tasks.length === 0) return 0;
    const done = tasks.filter(t => t.completed).length;
    return Math.round((done / tasks.length) * 100);
  };

  return (
    <div data-testid="projects-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-2">Projetos</div>
          <h1 className="font-serif text-4xl md:text-5xl">Gestão de Projetos</h1>
          <p className="text-[#4A4A4A] mt-2">Prazos, status e detalhes em um só lugar.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { if(!v) resetForm(); setOpen(v); }}>
          {!isRevisor && (
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white" data-testid="new-project-btn">
                <Plus className="w-4 h-4 mr-2" /> Novo projeto
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{editingId ? "Editar projeto" : "Novo projeto"}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1"><Label className="text-xs font-mono uppercase">Cliente</Label>
                  <Input value={form.cliente} onChange={(e) => setForm({ ...form, cliente: e.target.value })} className="rounded-none" data-testid="project-client" disabled={isRevisor} /></div>
                <div className="space-y-1"><Label className="text-xs font-mono uppercase">Serviço</Label>
                  <Input value={form.servico} onChange={(e) => setForm({ ...form, servico: e.target.value })} className="rounded-none" disabled={isRevisor} /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1"><Label className="text-xs font-mono uppercase">Valor (R$)</Label>
                  <Input type="text" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} className="rounded-none h-12 text-lg" disabled={isRevisor} /></div>
                <div className="space-y-1"><Label className="text-xs font-mono uppercase">Prazo</Label>
                  <Input type="date" value={form.prazo} onChange={(e) => setForm({ ...form, prazo: e.target.value })} className="rounded-none" disabled={isRevisor} /></div>
                <div className="space-y-1"><Label className="text-xs font-mono uppercase">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>

              {!isRevisor && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1"><Label className="text-xs font-mono uppercase">Atribuir Revisor</Label>
                    <Select value={form.revisor_id} onValueChange={(v) => setForm({ ...form, revisor_id: v })}>
                      <SelectTrigger className="rounded-none"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nenhum">Nenhum</SelectItem>
                        {team.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label className="text-xs font-mono uppercase">Link do Arquivo</Label>
                    <Input value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value })} className="rounded-none" placeholder="Drive, Dropbox..." />
                  </div>
                </div>
              )}
              {isRevisor && form.file_url && (
                <div className="mt-2 border-t pt-4">
                  <a href={form.file_url} target="_blank" rel="noreferrer">
                    <Button type="button" variant="outline" className="rounded-none w-full border-blue-500 text-blue-600 hover:bg-blue-50">
                      <LinkIcon className="w-4 h-4 mr-2" /> Baixar Arquivos do Projeto (Gerente)
                    </Button>
                  </a>
                </div>
              )}
              
              {editingId && (
                <div className="mt-4 pt-4 border-t space-y-4">
                  <h4 className="font-serif text-lg text-[#D9381E]">Entrega do Trabalho</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs font-mono uppercase">Link do Trabalho Revisado</Label>
                      <Input value={form.delivery_url} onChange={(e) => setForm({ ...form, delivery_url: e.target.value })} className="rounded-none" placeholder="Link para Google Drive, Dropbox, etc..." disabled={!isRevisor} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-mono uppercase">Observações do Revisor</Label>
                      <textarea 
                        value={form.revisor_comments} 
                        onChange={(e) => setForm({ ...form, revisor_comments: e.target.value })} 
                        className="flex min-h-[80px] w-full border border-slate-200 bg-transparent px-3 py-2 text-sm placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50 rounded-none" 
                        placeholder="Comentários sobre a revisão, dúvidas, etc..."
                        disabled={!isRevisor}
                      />
                    </div>
                    {!isRevisor && form.delivery_url && (
                      <a href={form.delivery_url} target="_blank" rel="noreferrer">
                        <Button type="button" variant="outline" className="rounded-none w-full border-green-500 text-green-600 hover:bg-green-50 mt-2">
                          <LinkIcon className="w-4 h-4 mr-2" /> Acessar Trabalho Revisado
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <Label className="text-xs font-mono uppercase block mb-3">Andamento do Processo (Checklist)</Label>
                <div className="flex gap-2 mb-4">
                  <Input 
                    placeholder="Nova tarefa..." 
                    value={newTask} 
                    onChange={(e) => setNewTask(e.target.value)} 
                    onKeyDown={(e) => e.key === "Enter" && addTask()}
                    className="rounded-none" 
                  />
                  <Button onClick={addTask} type="button" variant="outline" className="rounded-none"><Plus className="w-4 h-4" /></Button>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                  {form.tasks.length === 0 && <p className="text-xs text-muted-foreground italic">Nenhuma tarefa adicionada.</p>}
                  {form.tasks.map((t) => (
                    <div key={t.id} className="flex items-center justify-between group bg-slate-50 p-2 border border-transparent hover:border-slate-200">
                      <div className="flex items-center gap-3">
                        <Checkbox checked={t.completed} onCheckedChange={(v) => setForm({ ...form, tasks: form.tasks.map(x => x.id === t.id ? { ...x, completed: !!v } : x) })} />
                        <span className={`text-sm ${t.completed ? "line-through text-muted-foreground" : ""}`}>{t.text}</span>
                      </div>
                      <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={() => removeTask(t.id)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <Button onClick={save} className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white w-full py-6 text-lg" data-testid="project-save-btn">
                {editingId ? "Salvar alterações" : "Criar projeto"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <Card className="p-16 text-center border border-black/10 rounded-none bg-white">
          <p className="text-[#8A8A8A] font-mono">Nenhum projeto ainda.</p>
        </Card>
      ) : (
        <div className="space-y-8">
          {Object.entries(items.reduce((acc, p) => {
            let monthYear = "Sem prazo";
            if (p.prazo) {
              const d = new Date(p.prazo);
              d.setMinutes(d.getMinutes() + d.getTimezoneOffset()); // Fix timezone shift
              monthYear = d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
              monthYear = monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
            }
            if (!acc[monthYear]) acc[monthYear] = [];
            acc[monthYear].push(p);
            return acc;
          }, {})).map(([monthYear, monthItems]) => (
            <div key={monthYear}>
              <h2 className="font-serif text-2xl text-[#D9381E] mb-4 pb-2 border-b border-black/10">{monthYear}</h2>
              <div className="grid gap-4">
                {monthItems.map((p) => {
                  const prog = getProgress(p.tasks);
            const isExpanded = expandedId === p.id;
            const assigned = team.find(t => t.id === p.revisor_id);
            return (
              <Card key={p.id} className="bg-white border border-black/10 rounded-none overflow-hidden transition-all hover:shadow-md" data-testid={`project-${p.id}`}>
                <div className="p-5">
                  <div className="flex flex-col md:flex-row items-start justify-between gap-4">
                    <div className="flex-1 w-full">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-serif text-xl flex items-center gap-2">
                          {p.cliente}
                          {!isRevisor && p.revisor_id && p.seen_by?.includes(p.revisor_id) && (
                            <span className="text-[10px] bg-green-100 text-green-800 px-1.5 py-0.5 rounded flex items-center gap-1 font-sans" title="Visualizado pelo Revisor">
                              <Eye className="w-3 h-3" /> Visualizado
                            </span>
                          )}
                        </h3>
                        <Badge className={`${STATUS[p.status]?.cls || ""} rounded-none font-normal`}>{STATUS[p.status]?.label || p.status}</Badge>
                      </div>
                      <div className="text-sm text-[#4A4A4A] font-mono mb-4 flex flex-wrap gap-2 items-center">
                        <span>{p.servico}</span>
                        <span>·</span>
                        <span>prazo {p.prazo ? new Date(p.prazo).toLocaleDateString("pt-BR") : "—"}</span>
                        {!isRevisor && (
                          <>
                            <span>·</span>
                            <span>{fmtBRL(p.valor)}</span>
                          </>
                        )}
                        {!isRevisor && p.revisor_id && p.revisor_id !== "nenhum" && (
                          <>
                            <span>·</span>
                            <span className="bg-slate-100 text-slate-700 px-1.5 rounded border border-slate-200">Resp: {assigned?.name || "Desconhecido"}</span>
                          </>
                        )}
                      </div>
                      
                      {/* Progress Section */}
                      <div className="space-y-1 max-w-md">
                        <div className="flex justify-between text-[10px] font-mono uppercase tracking-tighter">
                          <span>Andamento</span>
                          <span>{prog}%</span>
                        </div>
                        <Progress value={prog} className="h-1 rounded-none bg-slate-100" />
                      </div>
                    </div>

                    <div className="flex flex-col items-start md:items-end gap-3 w-full md:w-auto mt-2 md:mt-0 pt-4 md:pt-0 border-t md:border-0 border-black/5">
                      <div className="flex items-center gap-2 w-full md:w-auto">
                        <Select value={p.status} onValueChange={(v) => updStatus(p.id, v)}>
                          <SelectTrigger className="rounded-none flex-1 md:w-36 h-9 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button variant="outline" size="icon" className="rounded-none h-9 w-9" onClick={() => edit(p)}>
                          <Pencil className="w-4 h-4 text-slate-600" />
                        </Button>
                        {!isRevisor && (
                          <Button variant="outline" size="icon" className="rounded-none h-9 w-9" onClick={() => rm(p.id)}>
                            <Trash2 className="w-4 h-4 text-[#D9381E]" />
                          </Button>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-[10px] font-mono uppercase h-auto py-2 px-3 rounded-none bg-slate-50 hover:bg-slate-100 w-full md:w-auto md:bg-transparent"
                        onClick={() => {
                          setExpandedId(isExpanded ? null : p.id);
                          if (!isExpanded) markAsSeen(p);
                        }}
                      >
                        {isExpanded ? "Ocultar detalhes" : "Ver detalhes"}
                        {isExpanded ? <ChevronUp className="ml-1 w-3 h-3" /> : <ChevronDown className="ml-1 w-3 h-3" />}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Collapsible Task List */}
                {isExpanded && (
                  <div className="border-t bg-slate-50/50 p-4 pt-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {(!p.tasks || p.tasks.length === 0) ? (
                        <p className="text-xs text-muted-foreground italic col-span-full">Sem tarefas para este projeto. Clique em editar para adicionar.</p>
                      ) : (
                        p.tasks.map((t) => (
                          <div 
                            key={t.id} 
                            className="flex items-center gap-2 p-2 bg-white border border-slate-100 rounded-none cursor-pointer hover:border-slate-300 transition-colors"
                            onClick={() => toggleTask(p, t.id)}
                          >
                            <div className={`w-4 h-4 border flex items-center justify-center transition-colors ${t.completed ? "bg-[#D9381E] border-[#D9381E]" : "border-slate-300"}`}>
                              {t.completed && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <span className={`text-xs truncate ${t.completed ? "line-through text-muted-foreground" : ""}`}>{t.text}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
